/**
 * Squirrel — the crow's thieving neighbour (Babylon.js port).
 *
 * Starts agitated (NPCBehavior "frantic": jittery scurrying) and is calmed
 * by Minna's Purr like a kitten — the calm bar fills while she purrs in
 * range, and a calmed squirrel stays calm. The Crow vs. Squirrel puzzle
 * keeps it at the foot of the crow's tree and, while it is agitated, has
 * it knock food out of the nest (`grabFood`).
 *
 * Detects nearby food items and rushes toward them.
 * Can be blocked by Basil's Pneumatic Anchor.
 *
 * Visual: small brown capsule (squirrel.glb shell).
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { NPC, NPCConfig } from "./NPC";
import { NPC_TUNING } from "../../config/GameConfig";

export class Squirrel extends NPC {
  override get species(): string { return "squirrel"; }

  /** Whether the squirrel is currently holding a food item. */
  private holdingFood = false;

  /** Detection radius for food items (world units). */
  private detectRadius: number;

  /** Timer to return to patrol after grabbing food (seconds). */
  private grabCooldown = 0;

  constructor(
    scene: Scene,
    key: string,
    position: Vector3,
    patrolPoints: number[],
  ) {
    const config: NPCConfig = {
      key,
      position,
      height: 0.7,
      radius: 0.22,
      color: '#8b6914',
      behavior: {
        initialState: 'frantic',
        calmRadius: NPC_TUNING.CALM_RADIUS,
        calmThreshold: NPC_TUNING.SQUIRREL.CALM_THRESHOLD,
        calmRate: NPC_TUNING.SQUIRREL.CALM_RATE,
        calmDecay: NPC_TUNING.SQUIRREL.CALM_DECAY,
        patrolPoints,
        patrolSpeed: NPC_TUNING.SQUIRREL.PATROL_SPEED,
      },
      modelPath: "/assets/models/npcs/squirrel.glb",
      modelScale: 3.0,
    };

    super(scene, config);
    this.detectRadius = NPC_TUNING.SQUIRREL.DETECT_RADIUS;

    this.material.diffuseColor = Color3.FromHexString('#8b6914');
  }

  /**
   * Check if any food items are nearby and return the closest one.
   * Food items must expose a `position: Vector3` and `isActive: boolean` shape.
   */
  checkForFood<T extends { position: Vector3; isActive: boolean }>(
    foodItems: T[],
  ): T | null {
    if (this.holdingFood) return null;

    let closest: T | null = null;
    let closestDist = this.detectRadius;

    for (const food of foodItems) {
      if (!food.isActive) continue;
      const dx = this.mesh.position.x - food.position.x;
      const dy = this.mesh.position.y - food.position.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < closestDist) {
        closestDist = dist;
        closest = food;
      }
    }

    return closest;
  }

  /** Rush toward a food item position (override patrol velocity). */
  rushToward(targetX: number, deltaSeconds: number): void {
    const dir = Math.sign(targetX - this.mesh.position.x);
    const rushSpeed = this.behavior.config.patrolSpeed * 1.8;
    this.mesh.position.x += dir * rushSpeed * deltaSeconds;

    if (dir < 0) this.mesh.rotation.y = Math.PI;
    else if (dir > 0) this.mesh.rotation.y = 0;
  }

  /** Grab a food item (the puzzle calls this when it snatches one). */
  grabFood(): void {
    this.holdingFood = true;
    this.grabCooldown = 2.0;
  }

  get isHoldingFood(): boolean {
    return this.holdingFood;
  }

  /**
   * Check if a point is blocked by an immovable object (Basil anchored).
   * @param blockerX  World X of the blocker.
   * @param blockerWidth  Width of the blocker in world units.
   */
  isPathBlocked(blockerX: number, blockerWidth: number): boolean {
    const direction = this.mesh.rotation.y === Math.PI ? -1 : 1;
    const aheadX = this.mesh.position.x + direction * 1.0;
    return Math.abs(aheadX - blockerX) < blockerWidth / 2 + 0.8;
  }

  override update(_time: number, deltaSeconds: number): void {
    // Grab cooldown. The behaviour state is left alone: resetting it here
    // would knock an agitated squirrel out of the calmable "frantic" state.
    if (this.holdingFood) {
      this.grabCooldown -= deltaSeconds;
      if (this.grabCooldown <= 0) {
        this.holdingFood = false;
      }
    }

    // Visual tint based on state
    if (this.holdingFood) {
      this.material.diffuseColor = Color3.FromHexString('#777766');
    } else {
      this.material.diffuseColor = Color3.FromHexString('#8b6914');
    }
  }
}
